// OpenSCAD-style customizer — parse top-level variables + leading/inline
// comments into UI-renderable descriptors.
//
// Supported syntax:
//   name = value;                      → form field
//   // Label comment                   → humanised label (applies to next var)
//   /* [Group] */                      → tab/section grouping
//   name = 10;   // [1:50]             → slider (min:max)
//   name = 10;   // [1:5:100]          → slider (min:step:max)
//   name = "a";  // [a, b, c]          → string dropdown
//   name = 1;    // [1, 2, 3]          → numeric dropdown
//   name = true;                       → checkbox
//   name = "text";                     → text input
//   name = [1, 2, 3];                  → text input (vector)
// Special OpenSCAD vars ($fn, $fa, $fs, $t) are skipped.

export type CustomizerFieldType =
  | 'number'
  | 'boolean'
  | 'string'
  | 'vector'
  | 'list-number'
  | 'list-string'
  | 'list-labeled'
  | 'range';

export interface LabeledOption {
  source: string; // value as it should appear in source (e.g. `3` or `"medium"`)
  label: string;  // what to show in the dropdown (e.g. `ogive`)
}

export interface CustomizerField {
  name: string;
  label: string;
  type: CustomizerFieldType;
  value: string;    // raw source value
  min?: number;
  max?: number;
  step?: number;
  options?: (string | number)[];
  labeledOptions?: LabeledOption[];
  from: number;     // offset where the RHS value starts in source
  to: number;       // offset where the RHS value ends (before ;)
  group?: string;
}

const SKIP_VARS = new Set(['$fn', '$fa', '$fs', '$t', '$preview', '$children']);

// Split a comma-separated string at top-level commas (ignoring those inside [], (), "", '')
function splitTopLevelCommas(s: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let inStr: '"' | "'" | null = null;
  let buf = '';
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (inStr) {
      buf += c;
      if (c === inStr && s[i - 1] !== '\\') inStr = null;
      continue;
    }
    if (c === '"' || c === "'") { inStr = c; buf += c; continue; }
    if (c === '[' || c === '(') { depth++; buf += c; continue; }
    if (c === ']' || c === ')') { depth--; buf += c; continue; }
    if (c === ',' && depth === 0) { out.push(buf); buf = ''; continue; }
    buf += c;
  }
  if (buf.length) out.push(buf);
  return out;
}

export function parseCustomizerFields(code: string): CustomizerField[] {
  const out: CustomizerField[] = [];
  const lines = code.split('\n');

  // Precompute byte offset of each line start
  const lineOffsets: number[] = [0];
  for (let i = 0; i < lines.length; i++) {
    lineOffsets.push(lineOffsets[i] + lines[i].length + 1);
  }

  let currentGroup: string | undefined;
  let pendingLabel: string | null = null;
  let blockDepth = 0; // track { } nesting — only scan top-level

  for (let i = 0; i < lines.length; i++) {
    const rawLine = lines[i];
    const line = rawLine.trim();

    // Update block depth from this line
    const openCount = (rawLine.match(/\{/g) || []).length;
    const closeCount = (rawLine.match(/\}/g) || []).length;

    // Only parse when we're at top level
    if (blockDepth > 0) {
      blockDepth += openCount - closeCount;
      if (blockDepth < 0) blockDepth = 0;
      pendingLabel = null;
      continue;
    }

    if (!line) {
      pendingLabel = null;
      // still count any braces for next line
      blockDepth += openCount - closeCount;
      if (blockDepth < 0) blockDepth = 0;
      continue;
    }

    // Group: /* [Name] */
    const groupMatch = line.match(/^\/\*\s*\[([^\]]+)\]\s*\*\/\s*;?\s*$/);
    if (groupMatch) {
      currentGroup = groupMatch[1].trim();
      pendingLabel = null;
      continue;
    }

    // Line comment: // text  (sets label for next var)
    const lineCommentMatch = line.match(/^\/\/\s*(.*)$/);
    if (lineCommentMatch) {
      pendingLabel = lineCommentMatch[1].trim();
      continue;
    }

    // Skip lines that look like a block-comment fragment
    if (line.startsWith('/*') || line.startsWith('*/')) {
      pendingLabel = null;
      continue;
    }

    // Variable assignment:  name = value ;  [optional inline // constraint]
    const assignMatch = rawLine.match(
      /^(\s*)([\$a-zA-Z_][a-zA-Z0-9_]*)\s*=\s*([^;]+?);\s*(\/\/\s*(.*))?$/
    );
    if (!assignMatch) {
      pendingLabel = null;
      blockDepth += openCount - closeCount;
      if (blockDepth < 0) blockDepth = 0;
      continue;
    }

    const indent = assignMatch[1];
    // Only top-level assignments (no leading spaces OR starts at column 0)
    if (indent.length > 0) {
      pendingLabel = null;
      blockDepth += openCount - closeCount;
      if (blockDepth < 0) blockDepth = 0;
      continue;
    }

    const name = assignMatch[2];
    const rawValue = assignMatch[3].trim();
    const inlineComment = (assignMatch[5] || '').trim();

    if (SKIP_VARS.has(name)) {
      pendingLabel = null;
      continue;
    }

    // Detect type from RHS — only accept LITERAL values. Computed expressions
    // (references to other variables, arithmetic, function calls) are dynamic
    // and should be edited in the source editor, not via the customizer.
    let type: CustomizerFieldType = 'string';
    let min: number | undefined;
    let max: number | undefined;
    let step: number | undefined;
    let options: (string | number)[] | undefined;

    const isBool = /^(true|false)$/.test(rawValue);
    const isNum = /^-?\d+(\.\d+)?$/.test(rawValue);
    const isStr = /^["'][^"']*["']$/.test(rawValue);
    // Vector literal: [ v1, v2, ... ] where every element is itself a literal
    // (number, bool, string) — reject any ref/expression.
    let isLiteralVec = false;
    if (/^\[.*\]$/.test(rawValue)) {
      const inner = rawValue.slice(1, -1).trim();
      if (inner === '') {
        isLiteralVec = true;
      } else {
        const items = splitTopLevelCommas(inner);
        isLiteralVec = items.every(item => {
          const t = item.trim();
          return (
            /^-?\d+(\.\d+)?$/.test(t) ||
            /^(true|false)$/.test(t) ||
            /^["'][^"']*["']$/.test(t)
          );
        });
      }
    }

    if (isBool) type = 'boolean';
    else if (isNum) type = 'number';
    else if (isStr) type = 'string';
    else if (isLiteralVec) type = 'vector';
    else {
      // Dynamic / computed — skip entirely.
      pendingLabel = null;
      continue;
    }
    // Remember the base type so a `[false, true]` list constraint on a bool
    // doesn't demote it to a dropdown.
    const baseType = type;

    // Parse inline constraint: [min:max] / [min:step:max] / [a, b, c] / [v1:lab1, v2:lab2]
    const constraint = inlineComment.match(/^\[([^\]]+)\]\s*$/);
    let labeledOptions: LabeledOption[] | undefined;
    if (constraint) {
      const inner = constraint[1].trim();
      const hasComma = inner.includes(',');

      if (!hasComma && inner.includes(':')) {
        // Plain range: [min:max] or [min:step:max]
        const parts = inner.split(':').map(p => parseFloat(p.trim()));
        if (parts.length === 2 && !parts.some(isNaN)) {
          [min, max] = parts;
          type = 'range';
        } else if (parts.length === 3 && !parts.some(isNaN)) {
          [min, step, max] = parts;
          type = 'range';
        }
      } else if (hasComma) {
        const items = inner.split(',').map(s => s.trim()).filter(Boolean);
        // A `[false, true]` (or `[true, false]`) constraint on a boolean
        // variable is just a redundant hint — keep the checkbox.
        const isBoolConstraint = (() => {
          const set = new Set(items.map(s => s.toLowerCase()));
          return set.size === 2 && set.has('true') && set.has('false');
        })();
        if (baseType === 'boolean' && isBoolConstraint) {
          // Leave type = 'boolean'; skip the rest of constraint parsing
        } else {
        const isLabeled = items.every(it => it.includes(':'));
        if (isLabeled) {
          // [val:label, val:label, ...]
          labeledOptions = items.map(it => {
            const idx = it.indexOf(':');
            const rawVal = it.slice(0, idx).trim();
            const label = it.slice(idx + 1).trim();
            // If val is numeric → store as-is; otherwise wrap in quotes for source
            const isNumericVal = /^-?\d+(\.\d+)?$/.test(rawVal);
            const alreadyQuoted = /^["'].*["']$/.test(rawVal);
            const source = isNumericVal || alreadyQuoted ? rawVal : `"${rawVal.replace(/^["']|["']$/g, '')}"`;
            return { source, label };
          });
          type = 'list-labeled';
        } else {
          const unquoted = items.map(s => s.replace(/^["']|["']$/g, ''));
          const allNumeric = unquoted.every(i => /^-?\d+(\.\d+)?$/.test(i));
          if (allNumeric) {
            options = unquoted.map(Number);
            type = 'list-number';
          } else {
            options = unquoted;
            type = 'list-string';
          }
        }
        } // end inner else (non-bool-constraint)
      }
    }

    // Label: pending comment else humanised name
    const label = pendingLabel || name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

    // Compute source offsets for the RHS
    const lineStart = lineOffsets[i];
    const eqPosInLine = rawLine.indexOf('=', rawLine.indexOf(name));
    const semiPosInLine = rawLine.indexOf(';', eqPosInLine);
    let from = lineStart + eqPosInLine + 1;
    while (from < lineStart + semiPosInLine && /\s/.test(code[from])) from++;
    const to = lineStart + semiPosInLine;

    out.push({
      name, label, type,
      value: rawValue,
      min, max, step,
      options,
      labeledOptions,
      from, to,
      group: currentGroup,
    });

    pendingLabel = null;
  }

  return out;
}
